import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, QuickFilters, DataTable, Segmented, Tag, Chip, Button, Dialog, ConfirmDialog, Field, TextInput, Select, Checkbox,
  ComboBox, Banner, Spinner, applyClientQuery, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import { formatHebrewDate } from '../../utils/hebrewDate';
import ImportSectionNext from '../../components/ImportSectionNext.jsx';
import { GemaraSelectionEditorNext, GemaraExamineEditDialogNext } from '../../components/GemaraNext.jsx';
import { EMPTY_TRACTATE_ENTRY, selectionToEntries, entriesToSelection, isGemaraEntriesValid } from '../../components/gemaraSelectionModel.js';
import api from '../../services/api.js';
import RegistrationsDashboard from './RegistrationsDashboard.jsx';
import {
  normalizeGemaraSelection, summarizeGemaraSelection, formatGemaraSelectionText, countDafimInRanges, totalDafimInSelection,
} from '../../utils/gemaraSelection.js';
import { useQuickCreate } from '../../next/quickCreate';
import { FormGrid, MultiCheck, Row, apiError } from '../../next/ui';

/**
 * הרשמות למבחנים — new design. Same data and endpoints as RegistrationsList: period switch, the registrations
 * dashboard, pinned search terms, examined toggle (or per-tractate dialog in open-gemara), edit / grade / single PDF /
 * cancel per row, CSV export (with full member details), detailed gemara CSV, examined export (Excel/PDF/ZIP with
 * graders' columns), copies summary (open-gemara), new (bulk) registration and the registrations import.
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SOURCE = { ivr: 'ימות', web: 'ווב', import: 'ייבוא' };
const STATUS = { registered: { label: 'רשום', tone: 'success' }, cancelled: { label: 'בוטל', tone: 'danger' } };
const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const EXTRA_MEMBER_COLUMNS = [
  { key: '_memberNumber', label: 'מספר חבר' },
  { key: '_memberFatherName', label: 'שם אב' },
  { key: '_memberFatherInLawName', label: 'שם חותן' },
  { key: '_memberPhoneAlt', label: 'טלפון נוסף' },
  { key: '_memberStreet', label: 'רחוב' },
  { key: '_memberBuilding', label: 'מספר בניין' },
  { key: '_memberApartment', label: 'דירה' },
  { key: '_memberKolel', label: 'כולל' },
  { key: '_memberPersonalCode', label: 'קוד אישי חבר' },
];
const EXAMINED_EXPORT_COLUMNS = [
  { value: 'firstName', label: 'שם פרטי' }, { value: 'lastName', label: 'שם משפחה' }, { value: 'idNumber', label: 'ת.ז.' },
  { value: 'personalCode', label: 'קוד אישי' }, { value: 'phone', label: 'טלפון' }, { value: 'phoneAlt', label: 'טלפון נוסף' },
  { value: 'city', label: 'עיר' }, { value: 'kolel', label: 'כולל' }, { value: 'location', label: 'מיקום מבחן' },
  { value: 'examCode', label: 'קוד מבחן' }, { value: 'examName', label: 'שם מבחן' }, { value: 'score', label: 'ציון' }, { value: 'notes', label: 'הערות' },
];
const DEFAULT_EXAMINED_EXPORT_COLUMNS = ['firstName', 'lastName', 'idNumber', 'phone', 'examCode', 'examName', 'score', 'notes'];
const MEMBER_KEYS = ['_memberFirstName', '_memberLastName', '_memberIdNumber', '_memberPhone', '_memberCity', '_memberKolel'];

const isExamined = (row) => (row?.status === 'registered') && (IS_OPEN_GEMARA ? (row.gemaraSelection || []).some((s) => s.examined) : !!row.examined);
const examinedMarkLabel = (row) => (isExamined(row) ? 'כן' : 'לא');
const extractPeriodId = (p) => (!p ? '' : typeof p === 'object' ? String(p._id || '') : String(p));
const examLabel = (o) => (o ? `${o.name} (${o.code}) — ${formatHebrewDate(o.date)}` : '');
const memberLabel = (o) => (o ? `${o.firstName} ${o.lastName}${o.phone ? ` (${o.phone})` : ''}${o.idNumber ? ` — ת.ז. ${o.idNumber}` : ''}` : '');

function download(blobOrData, filename, mime) {
  const url = URL.createObjectURL(blobOrData instanceof Blob ? blobOrData : new Blob([blobOrData], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const csvBlob = (lines) => new Blob([`﻿${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });

function mapRow(r) {
  const m = r.memberId || {};
  return {
    ...r,
    _memberFirstName: m.firstName || '',
    _memberLastName: m.lastName || '',
    _memberName: r.memberId ? `${m.firstName || ''} ${m.lastName || ''}`.trim() : '',
    _memberIdNumber: m.idNumber || '',
    _memberPhone: m.phone || '',
    _memberPhoneAlt: m.phoneAlt || '',
    _memberCity: m.city || '',
    _memberStreet: m.street || '',
    _memberBuilding: m.buildingNumber || '',
    _memberApartment: m.apartment || '',
    _memberFatherName: m.fatherName || '',
    _memberFatherInLawName: m.fatherInLawName || '',
    _memberKolel: m.kolel || m.kolelMorning || m.kolelAfternoon || '',
    _memberPersonalCode: m.personalCode ?? '',
    _memberNumber: m.memberNumber || '',
    _examCode: r.examId?.code != null ? String(r.examId.code) : '',
    _examName: r.examId?.name || '',
    _kolelCode: r.examId?.kolelCode != null ? String(r.examId.kolelCode) : '',
    _gemaraText: formatGemaraSelectionText(r.gemaraSelection) || '',
    _totalDafim: totalDafimInSelection(r.gemaraSelection),
    _examinedMark: examinedMarkLabel(r),
  };
}

export default function RegistrationsListNext() {
  const toast = useCkToast();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('_active');
  const [examCities, setExamCities] = useState([]);
  const [quick, setQuick] = useState('all');
  const [pinnedSearches, setPinnedSearches] = useState([]);
  const [query, setQuery] = useState({ page: 1, pageSize: 50, search: '', sort: null, filters: {} });

  const [cancelTarget, setCancelTarget] = useState(null);
  const [examineRow, setExamineRow] = useState(null);
  const [examineSaving, setExamineSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [editTarget, setEditTarget] = useState(null);
  const [editEntries, setEditEntries] = useState([EMPTY_TRACTATE_ENTRY()]);
  const [editExam, setEditExam] = useState(null);
  const [editLocation, setEditLocation] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [openExams, setOpenExams] = useState([]);

  const [gradeRow, setGradeRow] = useState(null);
  const [gradeScores, setGradeScores] = useState({});
  const [gradeSaving, setGradeSaving] = useState(false);
  const [gradeLoading, setGradeLoading] = useState(false);
  const [gradeError, setGradeError] = useState('');

  const [regOpen, setRegOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [regLocation, setRegLocation] = useState('');
  const [regEntries, setRegEntries] = useState([EMPTY_TRACTATE_ENTRY()]);
  const [regSaving, setRegSaving] = useState(false);
  const [regResult, setRegResult] = useState(null);

  const [csvDialog, setCsvDialog] = useState(null); // 'csv' | 'detailed'
  const [csvFullDetails, setCsvFullDetails] = useState(false);
  const [materialExporting, setMaterialExporting] = useState('');
  const [exOpen, setExOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [ex, setEx] = useState({ mode: 'examined', format: 'excel', fileMode: 'single', columns: DEFAULT_EXAMINED_EXPORT_COLUMNS, blanks: [], newBlank: '' });

  const lastYear = new Date();
  lastYear.setFullYear(lastYear.getFullYear() - 1);
  const [importDate, setImportDate] = useState(lastYear.toISOString().slice(0, 10));
  const [importPeriodId, setImportPeriodId] = useState('');
  const [importUpdateMode, setImportUpdateMode] = useState(false);

  /* ── data ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get('/registrations', { params: { page: 1, pageSize: 100000 } });
      setAllData((res.data || []).map(mapRow));
    } catch (e) {
      setError(apiError(e, 'שגיאה בשליפת הרשמות'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    api.get('/exam-cities?active=1').then(({ data }) => setExamCities((data.data || []).map((c) => c.name).filter(Boolean))).catch(() => setExamCities([]));
    api.get('/exam-periods').then(({ data }) => {
      const list = data.data || [];
      setPeriods(list);
      const active = list.find((p) => p.isActive) || list[list.length - 1];
      if (active) setPeriodFilter(active._id);
    }).catch(() => {});
  }, []);
  const loadOpenExams = () => api.get('/exams', { params: { pageSize: 200, status: 'open' } }).then(({ data: res }) => setOpenExams(res.data || [])).catch(() => setOpenExams([]));

  const effectivePeriodId = useMemo(() => {
    if (periodFilter === '_all') return null;
    if (periodFilter === '_active') return periods.find((p) => p.isActive)?._id || null;
    return periodFilter;
  }, [periodFilter, periods]);
  const effectivePeriodLabel = useMemo(() => (periodFilter === '_all' ? null : periods.find((x) => x._id === effectivePeriodId)?.name || null), [periodFilter, effectivePeriodId, periods]);

  /* ── examined ── */
  const toggleExamined = useCallback(async (row) => {
    const next = !row.examined;
    const apply = (r, v) => ({ ...r, examined: v, _examinedMark: examinedMarkLabel({ ...r, examined: v }) });
    setAllData((prev) => prev.map((r) => (r._id === row._id ? apply(r, next) : r)));
    try {
      const { data: updated } = await api.put(`/registrations/${row._id}`, { examined: next });
      setAllData((prev) => prev.map((r) => (r._id === updated._id ? apply(r, updated.examined) : r)));
    } catch {
      setAllData((prev) => prev.map((r) => (r._id === row._id ? apply(r, row.examined) : r)));
      toast.error('שגיאה בעדכון סטטוס בחינה');
    }
  }, [toast]);
  const saveExamine = async (gemaraSelection) => {
    if (!examineRow) return;
    setExamineSaving(true);
    try {
      const { data: updated } = await api.put(`/registrations/${examineRow._id}`, { gemaraSelection });
      setAllData((prev) => prev.map((r) => (r._id === updated._id ? mapRow({ ...r, gemaraSelection: updated.gemaraSelection }) : r)));
      toast.success('סטטוס בחינה עודכן');
      setExamineRow(null);
    } catch {
      toast.error('שגיאה בשמירת סטטוס בחינה');
    } finally {
      setExamineSaving(false);
    }
  };

  /* ── edit ── */
  const openEdit = (row) => {
    setEditTarget(row);
    setEditLocation(row.location || '');
    if (IS_OPEN_GEMARA) setEditEntries(selectionToEntries(row.gemaraSelection));
    else { setEditExam(row.examId || null); loadOpenExams(); }
  };
  const saveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const body = { location: editLocation };
      if (IS_OPEN_GEMARA) body.gemaraSelection = entriesToSelection(editEntries);
      else if (editExam) body.examId = editExam._id;
      await api.put(`/registrations/${editTarget._id}`, body);
      toast.success('ההרשמה עודכנה בהצלחה');
      setEditTarget(null);
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון ההרשמה'));
    } finally {
      setEditSaving(false);
    }
  };

  /* ── grade ── */
  const openGrade = useCallback(async (row) => {
    setGradeRow(row);
    setGradeError('');
    setGradeLoading(true);
    try {
      const memberId = row.memberId?._id || row.memberId;
      const examId = row.examId?._id || row.examId;
      const examPeriodId = extractPeriodId(row.periodId);
      const params = { memberId, all: 1 };
      if (IS_OPEN_GEMARA && examPeriodId) params.examPeriodId = examPeriodId;
      else if (examId) params.examId = examId;
      const { data } = await api.get('/grades', { params });
      const grades = data?.data || [];
      if (IS_OPEN_GEMARA) {
        const scores = {};
        (row.gemaraSelection || []).forEach((sel) => { const existing = grades.find((g) => g.tractate === sel.tractate) || null; scores[sel.tractate] = { score: existing?.score ?? '', existing }; });
        setGradeScores(scores);
      } else {
        const existing = grades.find((g) => !g.tractate) || null;
        setGradeScores({ '': { score: existing?.score ?? '', existing } });
      }
    } catch {
      setGradeError('שגיאה בטעינת ציונים');
    } finally {
      setGradeLoading(false);
    }
  }, []);
  const saveGrade = async () => {
    if (!gradeRow) return;
    setGradeError('');
    const memberId = gradeRow.memberId?._id || gradeRow.memberId;
    const examId = gradeRow.examId?._id || gradeRow.examId;
    const examPeriodId = extractPeriodId(gradeRow.periodId);
    if (IS_OPEN_GEMARA && !examPeriodId) { setGradeError('לא נמצא מועד מבחן להרשמה זו'); return; }
    setGradeSaving(true);
    const errs = [];
    let saved = 0;
    for (const [tractate, { score: raw, existing }] of Object.entries(gradeScores)) { // eslint-disable-line no-restricted-syntax
      if (raw === '' || raw == null) continue; // eslint-disable-line no-continue
      const s = parseFloat(raw);
      if (Number.isNaN(s) || s < 0) { errs.push(`${tractate || 'ציון'}: ציון לא תקין`); continue; } // eslint-disable-line no-continue
      try {
        if (existing) {
          if (existing.score === s) continue; // eslint-disable-line no-continue
          await api.put(`/grades/${existing._id}`, { score: s, tractate: tractate || null, version: existing.version }); // eslint-disable-line no-await-in-loop
        } else {
          await api.post('/grades', IS_OPEN_GEMARA ? { memberId, examPeriodId, score: s, tractate: tractate || null } : { memberId, examId, score: s, tractate: tractate || null }); // eslint-disable-line no-await-in-loop
        }
        saved += 1;
      } catch (e) {
        errs.push(e.response?.data?.error || 'שגיאה');
      }
    }
    setGradeSaving(false);
    if (!errs.length) { toast.success(saved ? 'ציון נשמר' : 'לא בוצע שינוי'); setGradeRow(null); } else setGradeError(errs.join(' | '));
  };

  /* ── single PDF / cancel ── */
  const downloadSingle = async (row) => {
    setDownloadingId(row._id);
    try {
      const { data } = await api.post('/exam-issuance/download-single', { registrationId: row._id, options: {} }, { responseType: 'blob' });
      download(data, `${row._memberName || 'בחינה'}.pdf`, 'application/pdf');
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'הרשמה לא נמצאה' : 'שגיאה בהורדת הבחינה');
    } finally {
      setDownloadingId(null);
    }
  };
  const cancelReg = async () => {
    try {
      await api.delete(`/registrations/${cancelTarget._id}`);
      toast.success('ההרשמה בוטלה');
      loadData();
    } catch {
      toast.error('שגיאה בביטול ההרשמה');
    } finally {
      setCancelTarget(null);
    }
  };

  /* ── new registration ── */
  const openReg = useCallback(() => {
    setRegOpen(true);
    setSelectedExam(null);
    setSelectedMembers([]);
    setRegLocation('');
    setRegResult(null);
    setRegEntries([EMPTY_TRACTATE_ENTRY()]);
    if (!IS_OPEN_GEMARA) loadOpenExams();
  }, []);
  useQuickCreate(openReg);
  const searchMembers = useCallback(async (q) => {
    const { data: res } = await api.get('/members', { params: { pageSize: 50, search: q } });
    return res.data || [];
  }, []);
  const saveReg = async () => {
    if (!selectedMembers.length) return;
    if (IS_OPEN_GEMARA ? !isGemaraEntriesValid(regEntries) : !selectedExam) return;
    setRegSaving(true);
    setRegResult(null);
    try {
      const body = IS_OPEN_GEMARA
        ? { gemaraSelection: entriesToSelection(regEntries), location: regLocation, source: 'web' }
        : { examId: selectedExam._id, location: regLocation, source: 'web' };
      if (selectedMembers.length === 1) {
        await api.post('/registrations', { memberId: selectedMembers[0]._id, ...body });
        toast.success('ההרשמה נוצרה בהצלחה');
        setRegOpen(false);
        loadData();
      } else {
        const { data: result } = await api.post('/registrations/bulk', { memberIds: selectedMembers.map((m) => m._id), ...body });
        setRegResult(result);
        if (result.created?.length) {
          toast[result.errors?.length ? 'error' : 'success'](`נרשמו ${result.created.length} חברים${result.skipped?.length ? `, ${result.skipped.length} דולגו` : ''}`);
          loadData();
        } else toast('לא נוצרו הרשמות חדשות');
      }
    } catch (e) {
      toast.error(apiError(e, 'שגיאה ביצירת הרשמה'));
    } finally {
      setRegSaving(false);
    }
  };

  /* ── columns ── */
  const columns = useMemo(() => [
    { key: '_memberFirstName', label: 'שם פרטי', render: (r) => <b>{r._memberFirstName || '—'}</b> },
    { key: '_memberLastName', label: 'שם משפחה', render: (r) => <b>{r._memberLastName || '—'}</b> },
    { key: '_memberIdNumber', label: 'ת.ז. חבר', ltr: true, copy: true },
    { key: '_memberPhone', label: 'טלפון חבר', ltr: true, copy: true },
    { key: '_memberCity', label: 'עיר חבר', type: 'enum' },
    { key: '_memberKolel', label: 'כולל', type: 'enum' },
    ...(IS_OPEN_GEMARA ? [
      { key: '_gemaraText', label: 'חומר', sortable: false, render: (r) => formatGemaraSelectionText(r.gemaraSelection, toHebrewNumeral) || '—' },
      { key: '_totalDafim', label: 'סה״כ דפים', type: 'number', getValue: (r) => r._totalDafim || null },
    ] : [{ key: '_examName', label: 'מבחן', type: 'enum' }]),
    { key: '_examCode', label: 'קוד מבחן', ltr: true, type: 'enum' },
    { key: '_kolelCode', label: 'קוד כולל', ltr: true },
    { key: 'location', label: 'עיר בחינה', type: 'enum' },
    {
      key: '_examinedMark',
      label: 'נבחן',
      type: 'enum',
      exportValue: (r) => (IS_OPEN_GEMARA ? ((r.gemaraSelection || []).filter((s) => s.examined).map((s) => s.tractate).join(', ') || 'לא') : (r.examined ? 'כן' : 'לא')),
      render: (r) => {
        if (r.status !== 'registered') return <span className="ck-muted">—</span>;
        if (IS_OPEN_GEMARA) {
          const n = (r.gemaraSelection || []).filter((s) => s.examined).length;
          const total = (r.gemaraSelection || []).length;
          return <Button size="sm" variant={n ? 'primary' : 'ghost'} icon="check" onClick={(e) => { e.stopPropagation(); setExamineRow(r); }}>{n ? `${n}/${total}` : 'סמן נבחן'}</Button>;
        }
        return <span onClick={(e) => e.stopPropagation()} role="presentation"><Checkbox checked={!!r.examined} onChange={() => toggleExamined(r)} label={r.examined ? 'נבחן' : ''} /></span>;
      },
    },
    { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => STATUS[r.status]?.label || r.status, render: (r) => <Tag tone={STATUS[r.status]?.tone || 'neutral'}>{STATUS[r.status]?.label || r.status}</Tag> },
    { key: 'source', label: 'מקור', type: 'enum', getValue: (r) => SOURCE[r.source] || r.source || '', render: (r) => <Tag icon={r.source === 'ivr' ? 'phone' : r.source === 'import' ? 'upload' : 'link'}>{SOURCE[r.source] || r.source}</Tag> },
    { key: 'personalCode', label: 'קוד אישי', ltr: true, getValue: (r) => String(r.personalCode ?? r._memberPersonalCode ?? '') },
    { key: 'registeredAt', label: 'תאריך רישום', type: 'date', render: (r) => (r.registeredAt ? formatHebrewDate(r.registeredAt) : '—'), exportValue: (r) => (r.registeredAt ? formatHebrewDate(r.registeredAt) : '') },
    ...(periodFilter === '_all' ? [{ key: 'period', label: 'זמן', type: 'enum' }] : []),
  ], [periodFilter, toggleExamined]);

  /* ── rows: period → quick → pinned → table query ── */
  const baseRows = useMemo(() => {
    let r = allData;
    if (periodFilter !== '_all') {
      const pid = periodFilter === '_active' ? periods.find((p) => p.isActive)?._id : periodFilter;
      if (pid) r = r.filter((row) => extractPeriodId(row.periodId) === String(pid));
    }
    if (quick === 'examined') r = r.filter((row) => row._examinedMark === 'כן');
    if (quick === 'registered') r = r.filter((row) => row.status === 'registered');
    if (quick === 'cancelled') r = r.filter((row) => row.status === 'cancelled');
    pinnedSearches.forEach((term) => { r = applyClientQuery(r, columns, { search: term, filters: {} }); });
    return r;
  }, [allData, periodFilter, periods, quick, pinnedSearches, columns]);
  const shownRows = useMemo(() => applyClientQuery(baseRows, columns, query), [baseRows, columns, query]);
  const counts = useMemo(() => {
    const pid = periodFilter === '_all' ? null : (periodFilter === '_active' ? periods.find((p) => p.isActive)?._id : periodFilter);
    const inPeriod = pid ? allData.filter((row) => extractPeriodId(row.periodId) === String(pid)) : allData;
    return {
      all: inPeriod.length,
      registered: inPeriod.filter((r) => r.status === 'registered').length,
      examined: inPeriod.filter((r) => r._examinedMark === 'כן').length,
      cancelled: inPeriod.filter((r) => r.status === 'cancelled').length,
    };
  }, [allData, periodFilter, periods]);

  const pinSearch = () => {
    const v = String(query.search || '').trim();
    if (!v || pinnedSearches.includes(v)) return;
    setPinnedSearches((p) => [...p, v]);
    setQuery((q) => ({ ...q, search: '', page: 1 }));
  };

  /* ── CSV exports (rows the table shows) ── */
  const exportCsv = (fullDetails) => {
    const base = columns.filter((c) => c.key !== 'period' || periodFilter === '_all');
    const lastMember = Math.max(...base.map((c, i) => (MEMBER_KEYS.includes(c.key) ? i : -1)));
    const cols = [...base.slice(0, lastMember + 1), ...(fullDetails ? EXTRA_MEMBER_COLUMNS : []), ...base.slice(lastMember + 1)];
    const value = (col, row) => {
      if (col.exportValue) return col.exportValue(row);
      if (col.getValue) return col.getValue(row);
      if (col.key === '_gemaraText') return row._gemaraText;
      return row[col.key];
    };
    const lines = [cols.map((c) => csvCell(c.label)).join(','), ...shownRows.map((row) => cols.map((c) => { const v = value(c, row); return csvCell(v && typeof v === 'object' ? JSON.stringify(v) : v); }).join(','))];
    download(csvBlob(lines), 'הרשמות_למבחנים.csv');
  };
  const exportDetailed = (fullDetails) => {
    let maxT = 0;
    const maxR = [];
    const norm = shownRows.map((row) => ({ row, gs: normalizeGemaraSelection(row.gemaraSelection) }));
    norm.forEach(({ gs }) => {
      maxT = Math.max(maxT, gs.length);
      gs.forEach((sel, t) => { const rc = (sel.ranges || []).length || 1; if (!maxR[t] || rc > maxR[t]) maxR[t] = rc; });
    });
    const headers = ['שם פרטי', 'שם משפחה', 'ת.ז.', 'טלפון', 'עיר', 'כולל', ...(fullDetails ? EXTRA_MEMBER_COLUMNS.map((c) => c.label) : []), 'קוד אישי', 'סטטוס', 'מקור', 'עיר בחינה', 'סה"כ דפים'];
    for (let t = 0; t < maxT; t += 1) {
      const sfx = maxT > 1 ? ` ${t + 1}` : '';
      headers.push(`מסכת${sfx}`);
      for (let r = 0; r < (maxR[t] || 1); r += 1) { const rs = (maxR[t] || 1) > 1 ? ` ${r + 1}` : ''; headers.push(`מדף${sfx}${rs}`, `עד דף${sfx}${rs}`); }
      headers.push(`סה"כ דפים${sfx}`);
    }
    const lines = [headers.map(csvCell).join(','), ...norm.map(({ row, gs }) => {
      const cells = [row._memberFirstName, row._memberLastName, row._memberIdNumber, row._memberPhone, row._memberCity, row._memberKolel,
        ...(fullDetails ? EXTRA_MEMBER_COLUMNS.map((c) => row[c.key] ?? '') : []),
        row.personalCode ?? '', STATUS[row.status]?.label || row.status || '', SOURCE[row.source] || row.source || '', row.location || '', summarizeGemaraSelection(gs).totalDafim || ''];
      for (let t = 0; t < maxT; t += 1) {
        const sel = gs[t];
        cells.push(sel?.tractate || '');
        for (let r = 0; r < (maxR[t] || 1); r += 1) { const rng = (sel?.ranges || [])[r]; cells.push(rng?.from ? toHebrewNumeral(rng.from) : '', rng?.to ? toHebrewNumeral(rng.to) : ''); }
        cells.push(sel ? (countDafimInRanges(sel.ranges) || '') : '');
      }
      return cells.map(csvCell).join(',');
    })];
    download(csvBlob(lines), 'הרשמות_מפורט.csv');
  };
  const exportMaterial = async (format) => {
    setMaterialExporting(format);
    try {
      const params = effectivePeriodId ? { periodIds: String(effectivePeriodId) } : {};
      const response = await api.get(format === 'pdf' ? '/registrations/export-material-pdf' : '/registrations/export-material-excel', { params, responseType: 'blob' });
      const day = new Date().toISOString().slice(0, 10);
      download(response.data, `סיכום_עותקים_תלמודא_${day}.${format === 'pdf' ? 'pdf' : 'xlsx'}`, format === 'pdf' ? 'application/pdf' : XLSX);
    } catch {
      toast.error('שגיאה בייצוא סיכום עותקים');
    } finally {
      setMaterialExporting('');
    }
  };
  const openExaminedExport = () => {
    if (!effectivePeriodId) { toast.error('יש לבחור תקופת מבחן לייצוא'); return; }
    setEx({ mode: 'examined', format: 'excel', fileMode: 'single', columns: [...DEFAULT_EXAMINED_EXPORT_COLUMNS], blanks: [], newBlank: '' });
    setExOpen(true);
  };
  const addBlank = () => {
    const name = ex.newBlank.trim().slice(0, 40);
    if (!name) return;
    setEx((e) => ({ ...e, blanks: e.blanks.includes(name) ? e.blanks : [...e.blanks, name], newBlank: '' }));
  };
  const runExaminedExport = async () => {
    if (!ex.columns.length && !ex.blanks.length) { toast.error('יש לבחור לפחות עמודה אחת'); return; }
    const isZip = ex.fileMode === 'byExam' || ex.fileMode === 'bySubject';
    setExporting(true);
    try {
      const response = await api.get(ex.format === 'pdf' ? '/registrations/export-examined-pdf' : '/registrations/export-examined-excel', {
        params: {
          periodId: String(effectivePeriodId),
          mode: ex.mode,
          fileMode: ex.fileMode,
          columns: EXAMINED_EXPORT_COLUMNS.map((c) => c.value).filter((k) => ex.columns.includes(k)).join(','),
          extraBlankColumns: ex.blanks.join(','),
        },
        responseType: 'blob',
      });
      const day = new Date().toISOString().slice(0, 10);
      const periodPart = (effectivePeriodLabel || 'תקופה').replace(/[\\/:*?"<>|]/g, '_');
      const prefix = ex.mode === 'graded' ? 'עם_ציונים' : 'נבחנים';
      if (isZip) download(response.data, `${prefix}_${ex.fileMode === 'bySubject' ? 'לפי_נושא' : 'לפי_קוד_מבחן'}_${periodPart}_${day}.zip`, 'application/zip');
      else download(response.data, `${prefix}_תקופה_${periodPart}_${day}.${ex.format === 'pdf' ? 'pdf' : 'xlsx'}`, ex.format === 'pdf' ? 'application/pdf' : XLSX);
      setExOpen(false);
    } catch {
      toast.error('שגיאה בייצוא נבחנים');
    } finally {
      setExporting(false);
    }
  };

  const cityOptions = (current) => [{ value: '', label: '— ללא —' }, ...[...new Set([...examCities, ...(current ? [current] : [])])].map((c) => ({ value: c, label: c }))];

  return (
    <>
      <PageHeader
        title="הרשמות למבחנים"
        sub={[effectivePeriodLabel, `${shownRows.length.toLocaleString('he-IL')} הרשמות`].filter(Boolean).join(' · ')}
        primary={{ label: 'הרשמה חדשה', onClick: openReg }}
        menus={[{
          key: 'export',
          label: 'ייצוא',
          icon: 'download',
          items: [
            { key: 'csv', label: 'ייצוא CSV', icon: 'download', disabled: !shownRows.length, onClick: () => { setCsvFullDetails(false); setCsvDialog('csv'); } },
            ...(IS_OPEN_GEMARA ? [{ key: 'detailed', label: 'ייצוא מפורט', icon: 'download', disabled: !shownRows.length, onClick: () => { setCsvFullDetails(false); setCsvDialog('detailed'); } }] : []),
            { key: 'examined', label: exporting ? 'מייצא…' : 'ייצוא נבחנים (לבודקים)', icon: 'check', disabled: !effectivePeriodId || exporting, onClick: openExaminedExport },
            ...(IS_OPEN_GEMARA ? [
              { divider: true },
              { key: 'm-xlsx', label: materialExporting === 'excel' ? 'מייצא…' : 'סיכום עותקים Excel', icon: 'download', disabled: !!materialExporting, onClick: () => exportMaterial('excel') },
              { key: 'm-pdf', label: materialExporting === 'pdf' ? 'מייצא…' : 'סיכום עותקים PDF', icon: 'file', disabled: !!materialExporting, onClick: () => exportMaterial('pdf') },
            ] : []),
          ],
        }]}
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: loadData }]}
      >
        {periods.length ? (
          <Segmented
            value={periodFilter === '_active' ? (effectivePeriodId || '_all') : periodFilter}
            onChange={setPeriodFilter}
            items={[...periods.map((p) => ({ value: p._id, label: `${p.name}${p.isActive ? ' ●' : ''}` })), { value: '_all', label: 'הכל' }]}
          />
        ) : null}
      </PageHeader>

      <RegistrationsDashboard periodId={effectivePeriodId} periodLabel={effectivePeriodLabel} />

      <QuickFilters
        value={quick}
        onChange={(k) => { setQuick((c) => (c === k ? 'all' : k)); setQuery((q) => ({ ...q, page: 1 })); }}
        loading={loading && !allData.length}
        items={[
          { key: 'all', label: 'כל ההרשמות', value: counts.all, icon: 'list', tone: 'neutral' },
          { key: 'registered', label: 'רשומים', value: counts.registered, icon: 'users', tone: 'primary' },
          { key: 'examined', label: 'נבחנים', value: counts.examined, icon: 'check', tone: 'success' },
          { key: 'cancelled', label: 'בוטלו', value: counts.cancelled, icon: 'x', tone: 'danger' },
        ]}
      />

      <DataTable
        mode="client"
        rows={baseRows}
        rowKey="_id"
        columns={columns}
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        query={query}
        onQueryChange={setQuery}
        storageKey="bchinot-registrations"
        layoutDefaults={{ pinned: ['_memberLastName'], hidden: ['_memberIdNumber', '_memberPhone', '_memberCity', '_memberKolel'] }}
        searchPlaceholder="חיפוש לפי שם, כולל, ת.ז., עיר… (נעיצה בכפתור)"
        toolbarEnd={<Button size="sm" variant="ghost" icon="pin" disabled={!String(query.search || '').trim()} onClick={pinSearch}>נעץ</Button>}
        extraChips={pinnedSearches.map((p) => ({ key: `pin-${p}`, label: `📌 ${p}`, icon: 'search', onRemove: () => setPinnedSearches((x) => x.filter((v) => v !== p)) }))}
        onClearAll={() => { setPinnedSearches([]); setQuick('all'); }}
        selectable={false}
        exportFilename="הרשמות_למבחנים"
        emptyTitle={quick === 'examined' ? 'אין נבחנים להצגה' : 'אין הרשמות'}
        onRowClick={(r) => { if (r.status === 'registered') openEdit(r); }}
        actionsWidth={170}
        rowActions={(r) => (r.status === 'registered' ? [
          { key: 'pdf', icon: 'download', label: downloadingId === r._id ? 'מוריד…' : 'הורד בחינה', onClick: () => { if (downloadingId !== r._id) downloadSingle(r); } },
          { key: 'edit', icon: 'edit', label: 'ערוך הרשמה', onClick: () => openEdit(r) },
          { key: 'grade', icon: 'task', label: 'הזן ציון', onClick: () => openGrade(r) },
          { key: 'cancel', icon: 'x', label: 'בטל הרשמה', onClick: () => setCancelTarget(r) },
        ] : [])}
      />

      <ImportSectionNext
        label="הרשמות"
        endpoint="/import/registrations"
        templateEndpoint="/import/template/registrations"
        description="ייבוא הרשמות מקובץ Excel. מבנה אופקי: קוד | שם | משפחת | טלפון | מסכת | מדף | עד דף | מדף | עד דף ... דפים במספרים."
        onImportDone={() => loadData()}
        uploadDisabled={!importPeriodId}
        uploadDisabledReason="יש לבחור זמן מבחנים לפני הייבוא"
        extraFormData={{ registeredAt: importDate, periodId: importPeriodId, ...(importUpdateMode ? { updateMode: 'true' } : {}) }}
        extraActions={(
          <div className="nx-stack" style={{ gap: 8 }}>
            <FormGrid cols={2}>
              <Field label="תאריך רישום"><TextInput type="date" ltr value={importDate} disabled={importUpdateMode} onChange={(e) => setImportDate(e.target.value)} /></Field>
              <Field label="זמן מבחנים (חובה)" error={!importPeriodId ? 'יש לבחור זמן מבחנים' : undefined}>
                <Select value={importPeriodId} onChange={setImportPeriodId} placeholder="בחרו זמן" options={periods.map((p) => ({ value: p._id, label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }))} />
              </Field>
            </FormGrid>
            <Checkbox checked={importUpdateMode} onChange={setImportUpdateMode} label="עדכון הרשמות קיימות (עדכן מדף / עד דף בלבד — לא יוצר הרשמות חדשות)" />
          </div>
        )}
      />

      {/* CSV */}
      <Dialog
        open={!!csvDialog}
        onClose={() => setCsvDialog(null)}
        title={csvDialog === 'detailed' ? 'ייצוא מפורט' : 'ייצוא הרשמות'}
        icon="download"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setCsvDialog(null)}>ביטול</Button>
            <Button variant="primary" icon="download" onClick={() => { if (csvDialog === 'detailed') exportDetailed(csvFullDetails); else exportCsv(csvFullDetails); setCsvDialog(null); }}>ייצא</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <p className="nx-desc">{`${shownRows.length} הרשמות ייוצאו לקובץ CSV (לפי הסינון והחיפוש הנוכחיים).`}</p>
          <Checkbox checked={csvFullDetails} onChange={setCsvFullDetails} label="כלול את כל פרטי הנרשם (ת.ז., טלפון, כתובת, כולל ועוד)" />
        </div>
      </Dialog>

      {/* examined export */}
      <Dialog
        open={exOpen}
        onClose={() => !exporting && setExOpen(false)}
        title="ייצוא נבחנים"
        icon="check"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={exporting} onClick={() => setExOpen(false)}>ביטול</Button>
            <Button variant="primary" icon="download" disabled={exporting || (!ex.columns.length && !ex.blanks.length)} onClick={runExaminedExport}>{exporting ? 'מייצא…' : 'ייצא'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <p className="nx-desc">רשימת מי שסומן כנבחן בתקופה הנבחרת — מחולקת לפי מבחן, עם עמודות ריקות לציון לבודקים.</p>
          <FormGrid cols={2}>
            <Field label="מי לייצא"><Segmented value={ex.mode} onChange={(v) => setEx((e) => ({ ...e, mode: v }))} items={[{ value: 'examined', label: 'נבחנים' }, { value: 'graded', label: 'עם ציונים' }]} /></Field>
            <Field label="פורמט"><Segmented value={ex.format} onChange={(v) => setEx((e) => ({ ...e, format: v }))} items={[{ value: 'excel', label: 'אקסל' }, { value: 'pdf', label: 'PDF' }]} /></Field>
          </FormGrid>
          <Field
            label="מצב קבצים"
            hint={ex.fileMode === 'bySubject' ? `יורד ZIP — קובץ ${ex.format === 'pdf' ? 'PDF' : 'אקסל'} לכל נושא (שבת, נידה וכו׳), לחלוקה לבודקים.` : ex.fileMode === 'byExam' ? `יורד ZIP — קובץ ${ex.format === 'pdf' ? 'PDF' : 'אקסל'} לכל קוד מבחן.` : undefined}
          >
            <Segmented value={ex.fileMode} onChange={(v) => setEx((e) => ({ ...e, fileMode: v }))} items={[{ value: 'single', label: 'קובץ אחד' }, { value: 'byExam', label: 'לפי קוד מבחן' }, { value: 'bySubject', label: 'לפי נושא' }]} />
          </Field>
          <Field label="עמודות"><MultiCheck options={EXAMINED_EXPORT_COLUMNS} value={ex.columns} onChange={(v) => setEx((e) => ({ ...e, columns: v }))} searchable={false} /></Field>
          <Field label="עמודות ריקות נוספות לבודקים">
            <Row>
              <TextInput value={ex.newBlank} placeholder="למשל: ציון חלק ב" onChange={(e) => setEx((x) => ({ ...x, newBlank: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBlank(); } }} style={{ flex: 1 }} />
              <Button size="sm" icon="plus" disabled={!ex.newBlank.trim()} onClick={addBlank}>הוסף</Button>
            </Row>
          </Field>
          {ex.blanks.length ? <Row>{ex.blanks.map((n) => <Chip key={n} onRemove={() => setEx((e) => ({ ...e, blanks: e.blanks.filter((b) => b !== n) }))}>{n}</Chip>)}</Row> : null}
        </div>
      </Dialog>

      {IS_OPEN_GEMARA ? (
        <GemaraExamineEditDialogNext
          key={examineRow?._id || 'examine'}
          open={!!examineRow}
          memberName={examineRow?._memberName}
          initialSelection={examineRow?.gemaraSelection || []}
          onSave={saveExamine}
          saving={examineSaving}
          onClose={() => !examineSaving && setExamineRow(null)}
        />
      ) : null}

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={cancelReg}
        danger
        title="ביטול הרשמה"
        confirmLabel="בטל הרשמה"
        message={`לבטל את הרשמת ${cancelTarget?.memberId ? `${cancelTarget.memberId.firstName} ${cancelTarget.memberId.lastName}` : ''}${cancelTarget?.examId ? ` למבחן ${cancelTarget.examId.name}` : ''}?`}
      />

      {/* edit */}
      <Dialog
        open={!!editTarget}
        onClose={() => !editSaving && setEditTarget(null)}
        title={`עריכת הרשמה — ${editTarget?._memberName || ''}`}
        icon="edit"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={editSaving} onClick={() => setEditTarget(null)}>ביטול</Button>
            <Button variant="primary" disabled={editSaving || (IS_OPEN_GEMARA && !isGemaraEntriesValid(editEntries))} onClick={saveEdit}>{editSaving ? 'שומר…' : 'שמור שינויים'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          {IS_OPEN_GEMARA
            ? <GemaraSelectionEditorNext value={editEntries} onChange={setEditEntries} showExamined title="עריכת חומר לבחינה" />
            : <Field label="מבחן"><ComboBox value={editExam} onChange={setEditExam} options={openExams} getLabel={examLabel} getKey={(o) => o?._id} placeholder="בחרו מבחן פתוח…" emptyText="אין מבחנים פתוחים" /></Field>}
          <Field label="עיר בחינה"><Select value={editLocation} onChange={setEditLocation} options={cityOptions(editLocation)} /></Field>
        </div>
      </Dialog>

      {/* grade */}
      <Dialog
        open={!!gradeRow}
        onClose={() => !gradeSaving && setGradeRow(null)}
        title={`ציון — ${gradeRow?._memberName || ''}`}
        icon="task"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={gradeSaving} onClick={() => setGradeRow(null)}>ביטול</Button>
            <Button variant="primary" disabled={gradeSaving || gradeLoading} onClick={saveGrade}>{gradeSaving ? 'שומר…' : 'שמור'}</Button>
          </>
        )}
      >
        {gradeLoading ? <div className="ck-empty"><Spinner size={26} /></div> : (
          <div className="nx-stack">
            {gradeError ? <Banner tone="danger" icon="alert">{gradeError}</Banner> : null}
            {IS_OPEN_GEMARA ? Object.entries(gradeScores).map(([tractate, { score, existing }]) => (
              <Row key={tractate}>
                <b style={{ minWidth: 110 }}>{tractate}</b>
                <TextInput type="number" ltr min={0} step={1} style={{ width: 120 }} value={score} onChange={(e) => setGradeScores((p) => ({ ...p, [tractate]: { ...p[tractate], score: e.target.value } }))} />
                {existing != null ? <small className="ck-muted">{`קיים: ${existing.score}`}</small> : null}
              </Row>
            )) : (() => {
              const entry = gradeScores[''] || { score: '', existing: null };
              return (
                <Field label="ציון" hint={entry.existing != null ? `ציון קיים: ${entry.existing.score}` : gradeRow?._examName}>
                  <TextInput type="number" ltr min={0} step={1} value={entry.score} data-autofocus onChange={(e) => setGradeScores({ '': { ...entry, score: e.target.value } })} onKeyDown={(e) => { if (e.key === 'Enter') saveGrade(); }} />
                </Field>
              );
            })()}
          </div>
        )}
      </Dialog>

      {/* new registration */}
      <Dialog
        open={regOpen}
        onClose={() => !regSaving && setRegOpen(false)}
        title="הרשמה למבחן"
        icon="plus"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={regSaving} onClick={() => setRegOpen(false)}>סגור</Button>
            <Button variant="primary" disabled={regSaving || !selectedMembers.length || (IS_OPEN_GEMARA ? !isGemaraEntriesValid(regEntries) : !selectedExam)} onClick={saveReg}>
              {regSaving ? 'רושם…' : `רשום${selectedMembers.length ? ` (${selectedMembers.length})` : ''}`}
            </Button>
          </>
        )}
      >
        <div className="nx-stack">
          {IS_OPEN_GEMARA
            ? <GemaraSelectionEditorNext value={regEntries} onChange={setRegEntries} showExamined={false} title="בחירת חומר לבחינה" />
            : <Field label="מבחן *"><ComboBox value={selectedExam} onChange={setSelectedExam} options={openExams} getLabel={examLabel} getKey={(o) => o?._id} placeholder="בחרו מבחן פתוח…" emptyText="אין מבחנים פתוחים" /></Field>}
          <Field label="חברים *" hint="הקלידו שם או טלפון לחיפוש; ניתן לבחור כמה חברים">
            <ComboBox
              value={null}
              onChange={(m) => { if (m && !selectedMembers.some((x) => x._id === m._id)) setSelectedMembers((p) => [...p, m]); }}
              onSearch={searchMembers}
              getLabel={memberLabel}
              getKey={(o) => o?._id}
              placeholder="הקלד שם או טלפון לחיפוש…"
              emptyText="לא נמצאו חברים"
            />
          </Field>
          {selectedMembers.length ? (
            <Row>{selectedMembers.map((m) => <Chip key={m._id} onRemove={() => setSelectedMembers((p) => p.filter((x) => x._id !== m._id))}>{`${m.firstName} ${m.lastName}`}</Chip>)}</Row>
          ) : null}
          <Field label="עיר בחינה"><Select value={regLocation} onChange={setRegLocation} options={cityOptions(regLocation)} /></Field>
          {selectedMembers.length > 1 ? <Banner tone="info" icon="info">{`ייווצרו ${selectedMembers.length} הרשמות בבת אחת`}</Banner> : null}
          {regResult?.created?.length ? <Banner tone="success" icon="check">{`נרשמו: ${regResult.created.map((c) => c.name).join(', ')}`}</Banner> : null}
          {regResult?.skipped?.length ? <Banner tone="warning" icon="alert">{`דולגו (כבר רשומים): ${regResult.skipped.map((s) => s.name).join(', ')}`}</Banner> : null}
          {regResult?.errors?.length ? <Banner tone="danger" icon="alert">{`שגיאות: ${regResult.errors.map((e) => `${e.memberId}: ${e.error}`).join(', ')}`}</Banner> : null}
        </div>
      </Dialog>
    </>
  );
}
